"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SmilePlus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui";

import { subscribePostReaction } from "@/lib/realtime/post-reaction-bus";
import { trpc } from "@/lib/trpc/client";
import { useGoogleAnalytics } from "@/components/google-analytics";

type ReactionItem = { emoji: string; memberId: string };

// Same quick set used by channel message reactions, for consistency.
const QUICK_EMOJIS = ["👍", "❤️", "👏", "🔥", "🎉", "👀"];

interface PostReactionBarProps {
  postId: string;
  /** Initial reactions. Only `emoji` + `memberId` are needed. */
  reactions: ReactionItem[];
  currentMemberId: string | null | undefined;
  disabled?: boolean;
}

/**
 * Single, exclusive emoji reaction control used in both the feed and the post
 * detail. The backend allows one reaction per member, so picking an emoji
 * replaces any existing one and picking the same emoji again removes it. There
 * is no separate "like" — ❤️ is just one of the emojis.
 *
 * Uses the same lightweight inline picker as channel message reactions (a small
 * row of quick emojis) rather than a Popover. The picker itself is portaled to
 * `document.body` and positioned from the trigger's bounding rect — feed post
 * cards clip overflowing children (rounded media corners), which previously
 * clipped a non-portaled picker anchored inside them (TRA-2392).
 */
export default function PostReactionBar({
  postId,
  reactions,
  currentMemberId,
  disabled = false,
}: PostReactionBarProps) {
  const [items, setItems] = useState<ReactionItem[]>(reactions);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const toggleReaction = trpc.posts.toggleReaction.useMutation();
  const googleAnalytics = useGoogleAnalytics();

  // Resync when the server data changes (e.g. revalidation).
  useEffect(() => {
    setItems(reactions);
  }, [reactions]);

  // Apply remote reactions live.
  useEffect(() => {
    return subscribePostReaction(postId, ({ memberId, emoji, removed }) => {
      setItems((prev) =>
        removed
          ? prev.filter((r) => r.memberId !== memberId)
          : [
              ...prev.filter((r) => r.memberId !== memberId),
              { emoji, memberId },
            ],
      );
    });
  }, [postId]);

  // Anchor the portaled picker to the trigger button's current position.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPickerPosition({ top: rect.bottom + 4, left: rect.left });
  }, [isOpen]);

  // Close the picker when clicking outside it (trigger or portaled content).
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !pickerRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen]);

  // The picker is positioned from a one-off rect read, not tracked live —
  // close it on scroll rather than let it drift away from the trigger.
  useEffect(() => {
    if (!isOpen) return;
    const onScroll = () => setIsOpen(false);
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [isOpen]);

  const grouped = Object.values(
    items.reduce(
      (acc, r) => {
        const entry = acc[r.emoji] ?? {
          emoji: r.emoji,
          count: 0,
          hasReacted: false,
        };
        entry.count += 1;
        if (r.memberId === currentMemberId) entry.hasReacted = true;
        acc[r.emoji] = entry;
        return acc;
      },
      {} as Record<
        string,
        { emoji: string; count: number; hasReacted: boolean }
      >,
    ),
  );

  const react = async (emoji: string) => {
    if (!currentMemberId || disabled) return;
    setIsOpen(false);

    const previous = items;
    const mine = items.find((r) => r.memberId === currentMemberId);
    const toggleOff = mine?.emoji === emoji;
    const withoutMine = items.filter((r) => r.memberId !== currentMemberId);
    setItems(
      toggleOff
        ? withoutMine
        : [...withoutMine, { emoji, memberId: currentMemberId }],
    );

    try {
      await toggleReaction.mutateAsync({ postId, emoji });
      if (!toggleOff) {
        try {
          googleAnalytics.event("reaction_added", { post_id: postId, emoji });
        } catch {}
      }
    } catch {
      setItems(previous);
    }
  };

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Reactions disabled for this channel/member: hide the add-reaction
       * trigger entirely rather than rendering a disabled-but-visible icon
       * (TRA-1836). */}
      {!disabled && (
        <div className="relative">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  ref={triggerRef}
                  type="button"
                  aria-label="Add a reaction"
                  onClick={(e) => {
                    stop(e);
                    setIsOpen((p) => !p);
                  }}
                  className="flex items-center rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <SmilePlus className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>React</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {isOpen &&
            pickerPosition &&
            createPortal(
              <div
                ref={pickerRef}
                style={{ top: pickerPosition.top, left: pickerPosition.left }}
                className="fixed z-[320] flex gap-1 rounded-md border border-border bg-popover p-1.5 shadow-lg"
              >
                {QUICK_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={(e) => {
                      stop(e);
                      void react(emoji);
                    }}
                    className="rounded p-1 text-base leading-none hover:bg-accent"
                  >
                    {emoji}
                  </button>
                ))}
              </div>,
              document.body,
            )}
        </div>
      )}

      {grouped.map(({ emoji, count, hasReacted }) => (
        <button
          key={emoji}
          type="button"
          disabled={disabled}
          onClick={(e) => {
            stop(e);
            void react(emoji);
          }}
          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
            hasReacted
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-card text-muted-foreground hover:bg-accent"
          }`}
        >
          <span>{emoji}</span>
          {count > 0 && <span>{count}</span>}
        </button>
      ))}
    </div>
  );
}
